import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { collection, query, where, orderBy, limit, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import ProductCard from "../components/shop/ProductCard";
import SlimAdBanner from "../components/ui/SlimAdBanner";
import { ArrowRight, Zap, Star } from "lucide-react";
import { CATEGORY_DETAILS } from "../utils/format";

/* ─── Static data ─────────────────────────────────────────────────────────── */
const TESTIMONIALS = [
  { name: "Chidi A.", role: "Business Owner, Lagos", rating: 5, text: "Ordered 3 inverters and 6 lithium batteries — arrived in 2 days, all genuine. The pricing is unbeatable compared to local markets." },
  { name: "Ngozi E.", role: "IT Manager, Abuja",    rating: 5, text: "Got 10 laptops for our office. Every single one was brand new, sealed. Afriport has earned a permanent customer." },
  { name: "Emeka O.", role: "Solar Installer, PH",  rating: 5, text: "The Starlink and solar panels arrived exactly as described. Delivery was super fast. I recommend Afriport to all my clients." },
];

/* ─── Section Header ──────────────────────────────────────────────────────── */
function SectionHeader({ eyebrow, title, cta, ctaHref }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 36, flexWrap: "wrap", gap: 12 }}>
      <div>
        <p style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: "var(--brand)", marginBottom: 6 }}>{eyebrow}</p>
        <h2 style={{ fontFamily: "var(--font-head)", fontSize: "clamp(22px, 3vw, 32px)", fontWeight: 800, color: "var(--text)", lineHeight: 1.15 }}>{title}</h2>
      </div>
      {cta && (
        <Link to={ctaHref} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 14, fontWeight: 600, color: "var(--brand)", padding: "8px 16px", borderRadius: "var(--radius-full)", border: "1.5px solid var(--brand)", transition: "all 0.2s", flexShrink: 0 }}
          onMouseEnter={e => { e.currentTarget.style.background = "var(--brand)"; e.currentTarget.style.color = "#fff"; }}
          onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "var(--brand)"; }}>
          {cta} <ArrowRight size={14} />
        </Link>
      )}
    </div>
  );
}

export default function HomePage() {
  const [featured, setFeatured]       = useState([]);
  const [newArrivals, setNewArrivals] = useState([]);
  const [loading, setLoading]         = useState(true);

  useEffect(() => {
    async function load() {
      try {
        let featList = [];
        let newArrivalsList = [];

        // 1. Featured Products
        try {
          const featSnap = await getDocs(
            query(collection(db, "products"), where("status", "==", "active"), where("featured", "==", true), limit(8))
          );
          featList = featSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        } catch (e) {
          console.warn("Featured products load notice:", e);
        }

        // 2. New Arrivals (with fallback while composite index is building)
        try {
          const newSnap = await getDocs(
            query(collection(db, "products"), where("status", "==", "active"), orderBy("createdAt", "desc"), limit(8))
          );
          newArrivalsList = newSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        } catch (idxError) {
          console.warn("Composite index not ready yet, falling back to memory sort:", idxError?.message);
          const fallbackSnap = await getDocs(
            query(collection(db, "products"), where("status", "==", "active"), limit(24))
          );
          newArrivalsList = fallbackSnap.docs
            .map(d => ({ id: d.id, ...d.data() }))
            .sort((a, b) => {
              const timeA = a.createdAt?.toMillis?.() || (a.createdAt?.seconds ? a.createdAt.seconds * 1000 : 0) || 0;
              const timeB = b.createdAt?.toMillis?.() || (b.createdAt?.seconds ? b.createdAt.seconds * 1000 : 0) || 0;
              return timeB - timeA;
            })
            .slice(0, 8);
        }

        setFeatured(featList);
        setNewArrivals(newArrivalsList);
      } catch (e) {
        console.error("Error loading products:", e);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  return (
    <div>

      {/* ══════════════════════════════════════════════════════════════════
          HERO SECTION
      ══════════════════════════════════════════════════════════════════ */}
      <header className="hero">
        <div className="hero-overlay" />
        <div className="hero-content">
          <span className="hero-badge">Nigeria's Trusted Lifestyle &amp; Home Hub</span>
          <h1>Affordable &amp; Durable Living Essentials</h1>
          <p>
            From solar power, inverters &amp; tech hardware to household appliances and furniture, we empower Nigerian homes and businesses with quality essentials built to last.
          </p>
          <div className="hero-buttons">
            <Link to="/shop" className="btn btn-primary btn-lg" style={{ gap: 8, fontSize: 16 }}>
              Explore Catalog <ArrowRight size={18} />
            </Link>
            <Link to="/solar-calculator" className="btn btn-secondary btn-lg" style={{ gap: 8, fontSize: 16 }}>
              Calculate Solar Needs <Zap size={18} color="#F5A623" />
            </Link>
          </div>
        </div>
      </header>

      {/* ══════════════════════════════════════════════════════════════════
          FEATURES HIGHLIGHTS BAR
      ══════════════════════════════════════════════════════════════════ */}
      <section className="features-bar">
        <div className="feature-item">
          <span className="feature-icon">🚚</span>
          <div>
            <h4>Nationwide Delivery</h4>
            <p>Prompt dispatch across all 36 States</p>
          </div>
        </div>
        <div className="feature-item">
          <span className="feature-icon">🛡️</span>
          <div>
            <h4>Verified Durability</h4>
            <p>Authentic quality backed by warranty</p>
          </div>
        </div>
        <div className="feature-item">
          <span className="feature-icon">💰</span>
          <div>
            <h4>Fair Factory Pricing</h4>
            <p>Unmatched value for families &amp; businesses</p>
          </div>
        </div>
        <div className="feature-item">
          <span className="feature-icon">📞</span>
          <div>
            <h4>Customer Support</h4>
            <p>Dedicated assistance via phone &amp; email</p>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════
          CATEGORIES PREVIEW
      ══════════════════════════════════════════════════════════════════ */}
      <section className="categories-section">
        <div className="container">
          <div className="section-header">
            <h2>Shop by Category</h2>
            <p>Explore our wide selection of essentials curated for modern living</p>
          </div>

          <div className="category-grid">
            {CATEGORY_DETAILS.map(cat => (
              <Link
                key={cat.slug}
                to={`/shop?category=${encodeURIComponent(cat.name)}`}
                className="category-card"
              >
                <div className="category-img-wrap">
                  <img src={cat.img} alt={cat.name} loading="lazy" />
                  {cat.badge && (
                    <span
                      className="category-badge"
                      style={cat.badgeColor ? { background: cat.badgeColor } : {}}
                    >
                      {cat.badge}
                    </span>
                  )}
                </div>
                <h3 style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 20 }}>{cat.icon}</span>
                  <span>{cat.name}</span>
                </h3>
                <p>{cat.desc}</p>
                <div className="cat-link">
                  <span>Explore {cat.subCount} Sub-categories</span>
                  <span>&rarr;</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Slim Ad Banner placement (Home Mid) */}
      <div className="container" style={{ padding: "0 clamp(16px, 3vw, 24px)", margin: "0 auto" }}>
        <SlimAdBanner placement="home_mid" />
      </div>

      {/* ══════════════════════════════════════════════════════════════════
          FEATURED PRODUCTS
      ══════════════════════════════════════════════════════════════════ */}
      {(loading || featured.length > 0) && (
        <section style={{ background: "#fff", padding: "80px 0" }}>
          <div className="container">
            <SectionHeader eyebrow="⭐ Curated Selection" title="Featured Products" cta="See All Featured" ctaHref="/shop?featured=true" />
            {loading ? (
              <div className="product-grid">
                {Array(4).fill(0).map((_, i) => (
                  <div key={i} style={{ borderRadius: "var(--radius-md)", overflow: "hidden", border: "1px solid var(--border)" }}>
                    <div className="skeleton" style={{ aspectRatio: "4/5" }} />
                    <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 8 }}>
                      <div className="skeleton" style={{ height: 12, width: "40%" }} />
                      <div className="skeleton" style={{ height: 16, width: "90%" }} />
                      <div className="skeleton" style={{ height: 14, width: "60%" }} />
                      <div className="skeleton" style={{ height: 20, width: "50%", marginTop: 4 }} />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="product-grid">
                {featured.map(p => <ProductCard key={p.id} product={p} />)}
              </div>
            )}
          </div>
        </section>
      )}

      {/* ══════════════════════════════════════════════════════════════════
          CALL TO ACTION BANNER (SOLAR ENERGY SOLUTION)
      ══════════════════════════════════════════════════════════════════ */}
      <section className="container">
        <div className="cta-banner">
          <div className="cta-content">
            <h2>Need a Tailored Solar Energy Solution?</h2>
            <p>
              Calculate your daily household electrical load in seconds and get an accurate system sizing recommendation.
            </p>
            <Link
              to="/solar-calculator"
              className="btn btn-primary"
              style={{ marginTop: 22, display: "inline-flex", alignItems: "center", gap: 8, padding: "14px 28px", fontSize: 16, fontWeight: 700 }}
            >
              Launch Solar Calculator <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════
          NEW ARRIVALS
      ══════════════════════════════════════════════════════════════════ */}
      {(loading || newArrivals.length > 0) && (
        <section style={{ background: "var(--surface)", padding: "80px 0" }}>
          <div className="container">
            <SectionHeader eyebrow="🆕 Just Arrived" title="New Arrivals" cta="See All New" ctaHref="/shop?sort=newest" />
            {loading ? (
              <div className="product-grid">
                {Array(4).fill(0).map((_, i) => (
                  <div key={i} style={{ borderRadius: "var(--radius-md)", overflow: "hidden", border: "1px solid var(--border)" }}>
                    <div className="skeleton" style={{ aspectRatio: "4/5" }} />
                    <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 8 }}>
                      <div className="skeleton" style={{ height: 12, width: "40%" }} />
                      <div className="skeleton" style={{ height: 16, width: "90%" }} />
                      <div className="skeleton" style={{ height: 20, width: "50%", marginTop: 4 }} />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="product-grid">
                {newArrivals.map(p => <ProductCard key={p.id} product={p} />)}
              </div>
            )}
          </div>
        </section>
      )}

      {/* ══════════════════════════════════════════════════════════════════
          TESTIMONIALS
      ══════════════════════════════════════════════════════════════════ */}
      <section style={{ background: "#fff", padding: "80px 0" }}>
        <div className="container">
          <SectionHeader eyebrow="💬 Customer Reviews" title="What Our Customers Say" />
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 280px), 1fr))", gap: 24 }}>
            {TESTIMONIALS.map((t, i) => (
              <div key={i} className="stagger-child" style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: "var(--radius-md)", padding: "clamp(18px, 4vw, 28px)", transition: "all 0.25s var(--ease)" }}
                onMouseEnter={e => { e.currentTarget.style.boxShadow = "var(--shadow-md)"; e.currentTarget.style.borderColor = "var(--brand)"; e.currentTarget.style.transform = "translateY(-4px)"; }}
                onMouseLeave={e => { e.currentTarget.style.boxShadow = ""; e.currentTarget.style.borderColor = "var(--border)"; e.currentTarget.style.transform = ""; }}>
                <div style={{ display: "flex", gap: 2, marginBottom: 16 }}>
                  {[1,2,3,4,5].map(s => <Star key={s} size={14} fill="#F5A623" color="#F5A623" />)}
                </div>
                <p style={{ fontSize: 14, lineHeight: 1.75, color: "var(--text-muted)", marginBottom: 20, fontStyle: "italic" }}>"{t.text}"</p>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ width: 38, height: 38, borderRadius: "50%", background: "linear-gradient(135deg, var(--brand), #FF6B35)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 14 }}>
                    {t.name[0]}
                  </div>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 14, fontFamily: "var(--font-head)" }}>{t.name}</div>
                    <div style={{ fontSize: 12, color: "var(--text-muted)" }}>{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════
          CTA — SELL WITH US
      ══════════════════════════════════════════════════════════════════ */}
      <section style={{ background: "var(--surface)", borderTop: "1px solid var(--border)", padding: "clamp(48px, 8vw, 80px) 0" }}>
        <div className="container">
          <div style={{ background: "linear-gradient(135deg, #0D1B2A, #162436)", borderRadius: "var(--radius-xl)", padding: "clamp(36px, 6vw, 60px) clamp(16px, 4vw, 48px)", textAlign: "center", position: "relative", overflow: "hidden" }}>
            <div style={{ position: "absolute", top: -80, right: -80, width: 300, height: 300, background: "radial-gradient(circle, rgba(232,71,26,0.2) 0%, transparent 70%)", borderRadius: "50%", pointerEvents: "none" }} />
            <div style={{ position: "absolute", bottom: -60, left: "30%", width: 200, height: 200, background: "radial-gradient(circle, rgba(245,166,35,0.15) 0%, transparent 70%)", borderRadius: "50%", pointerEvents: "none" }} />
            <div style={{ position: "relative" }}>
              <p style={{ fontSize: 13, color: "rgba(255,255,255,0.6)", textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 600, marginBottom: 14 }}>🤝 BECOME A SELLER</p>
              <h2 style={{ fontFamily: "var(--font-head)", fontSize: "clamp(26px, 4vw, 52px)", fontWeight: 900, color: "#fff", marginBottom: 16, lineHeight: 1.15, wordBreak: "break-word", overflowWrap: "break-word" }}>
                Have products to sell?<br />
                <span style={{ background: "linear-gradient(90deg, #E8471A, #F5A623)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>
                  Earn 90% of every sale.
                </span>
              </h2>
              <p style={{ fontSize: 17, color: "rgba(255,255,255,0.7)", marginBottom: 36, maxWidth: 520, margin: "0 auto 36px", lineHeight: 1.7 }}>
                Register as a business seller, submit your products, and let Afriport handle shipping and customer service. We'll pay you instantly.
              </p>
              <Link to="/register" className="btn btn-primary btn-xl" style={{ fontWeight: 700, gap: 10 }}>
                Start Selling Today <ArrowRight size={20} />
              </Link>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
}
